import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Add, Delete, ExpandLess, ExpandMore, Save } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../models/apiError';
import {
    LAYOUT_HINT_OPTIONS,
    LibrarySection,
    LibrarySectionLayoutHint,
    LibrarySectionType,
    SectionTranslation,
} from '../../types/library';
import {
    useDeleteLibrarySection,
    useDynamicRules,
    useLibrarySections,
    useNormalizeLibrarySections,
    useUpdateLibrarySection,
} from '../../hooks/useLibrarySection';
import { LibrarySectionAddDialog } from './LibrarySectionAddDialog';
import { LIBRARY_SECTION_ICON_OPTIONS } from '../../types/librarySectionIcons';

const TYPE_FILTER_OPTIONS: Array<{ value: '' | LibrarySectionType; label: string }> = [
    { value: '', label: '전체' },
    { value: 'CATEGORY', label: 'CATEGORY' },
    { value: 'CURATION', label: 'CURATION' },
    { value: 'DYNAMIC', label: 'DYNAMIC' },
];

const TRANSLATION_LANGS: Array<{ lang: string; label: string }> = [
    { lang: 'ko', label: '한국어' },
    { lang: 'en', label: 'English' },
    { lang: 'ja', label: '日本語' },
    { lang: 'vi', label: 'Tiếng Việt' },
];

const TYPE_CHIP_COLOR: Record<
    LibrarySectionType,
    'primary' | 'secondary' | 'info'
> = {
    CATEGORY: 'primary',
    CURATION: 'secondary',
    DYNAMIC: 'info',
};

interface RowState {
    sortOrder: number;
    layoutHint: LibrarySectionLayoutHint;
    ruleCode: string | null;
    icon: string;
    isActive: boolean;
    translations: Record<string, string>;
}

const buildRowState = (s: LibrarySection): RowState => {
    const translations: Record<string, string> = {};
    for (const lang of TRANSLATION_LANGS) {
        const t = s.translations.find((x) => x.lang === lang.lang);
        translations[lang.lang] = t?.displayName ?? '';
    }
    return {
        sortOrder: s.sortOrder,
        layoutHint: s.layoutHint,
        ruleCode: s.ruleCode,
        icon: s.icon ?? '',
        isActive: s.isActive,
        translations,
    };
};

interface DeleteBlockedDialogState {
    open: boolean;
    sectionId: number | null;
    sectionType: LibrarySectionType | null;
    message: string;
}

export const LibrarySectionList: FC = () => {
    const navigate = useNavigate();
    const [typeFilter, setTypeFilter] = useState<'' | LibrarySectionType>('');
    const { data, isLoading, error } = useLibrarySections(
        typeFilter === '' ? undefined : typeFilter
    );
    const updateSection = useUpdateLibrarySection();
    const normalizeSections = useNormalizeLibrarySections();
    const deleteSection = useDeleteLibrarySection();
    const dynamicRulesQuery = useDynamicRules();
    const dynamicRules = dynamicRulesQuery.data?.rules ?? [];

    const [rowState, setRowState] = useState<Record<number, RowState>>({});
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [deleteBlocked, setDeleteBlocked] = useState<DeleteBlockedDialogState>({
        open: false,
        sectionId: null,
        sectionType: null,
        message: '',
    });

    useEffect(() => {
        if (!data) return;
        const next: Record<number, RowState> = {};
        for (const s of data) {
            next[s.id] = buildRowState(s);
        }
        setRowState(next);
    }, [data]);

    const sortedSections = useMemo(() => {
        if (!data) return [];
        return [...data].sort(
            (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
        );
    }, [data]);

    const nextSortOrder = useMemo(() => {
        if (!data || data.length === 0) return 1;
        return Math.max(...data.map((s) => s.sortOrder)) + 1;
    }, [data]);

    const handleChange = <K extends keyof RowState>(
        id: number,
        key: K,
        value: RowState[K]
    ) => {
        setRowState((prev) => ({
            ...prev,
            [id]: { ...prev[id], [key]: value },
        }));
    };

    const handleTranslationChange = (
        id: number,
        lang: string,
        value: string
    ) => {
        setRowState((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                translations: { ...prev[id].translations, [lang]: value },
            },
        }));
    };

    const handleSaveRow = async (s: LibrarySection) => {
        const state = rowState[s.id];
        if (!state) return;
        const translations: SectionTranslation[] = TRANSLATION_LANGS.map((l) => ({
            lang: l.lang,
            displayName: state.translations[l.lang] ?? '',
        })).filter((t) => t.displayName.trim().length > 0);

        try {
            const iconTrimmed = state.icon.trim();
            await updateSection.mutateAsync({
                id: s.id,
                data: {
                    sortOrder: state.sortOrder,
                    layoutHint: state.layoutHint,
                    ruleCode:
                        s.type === 'DYNAMIC' && state.ruleCode
                            ? state.ruleCode
                            : undefined,
                    icon: iconTrimmed.length > 0 ? iconTrimmed : undefined,
                    clearIcon: iconTrimmed.length === 0,
                    isActive: state.isActive,
                    translations,
                },
            });
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : '저장 실패';
            alert(`저장 실패: ${message}`);
        }
    };

    const handleNormalize = async () => {
        if (!confirm('현재 정렬 순서대로 sortOrder를 1부터 다시 매깁니다. 진행하시겠습니까?')) return;
        try {
            await normalizeSections.mutateAsync();
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : '재정렬 실패';
            alert(`재정렬 실패: ${message}`);
        }
    };

    const handleDelete = async (s: LibrarySection) => {
        if (!confirm(`섹션 "${s.code}" 를 삭제하시겠습니까?`)) return;
        try {
            await deleteSection.mutateAsync(s.id);
        } catch (e) {
            if (e instanceof ApiError && (e.status === 409 || e.errorCode === 13)) {
                setDeleteBlocked({
                    open: true,
                    sectionId: s.id,
                    sectionType: s.type,
                    message: e.message ?? '',
                });
            } else {
                const message = e instanceof Error ? e.message : '삭제 실패';
                alert(`삭제 실패: ${message}`);
            }
        }
    };

    const goToBlockedLibraries = () => {
        if (!deleteBlocked.sectionId || !deleteBlocked.sectionType) return;
        const param =
            deleteBlocked.sectionType === 'CURATION'
                ? 'curationSectionId'
                : 'categorySectionId';
        navigate(`/library?${param}=${deleteBlocked.sectionId}`);
        setDeleteBlocked({
            open: false,
            sectionId: null,
            sectionType: null,
            message: '',
        });
    };

    if (isLoading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="60vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">섹션 목록을 불러올 수 없습니다.</Alert>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
            >
                <Typography variant="h4">라이브러리 섹션 관리</Typography>
                <Stack direction="row" gap={1}>
                    <Button
                        variant="outlined"
                        onClick={handleNormalize}
                        disabled={normalizeSections.isPending}
                    >
                        sort_order 1부터 재정렬
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setAddDialogOpen(true)}
                    >
                        섹션 추가
                    </Button>
                </Stack>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                CATEGORY / CURATION / DYNAMIC 3종 섹션을 한 곳에서 관리합니다. DYNAMIC
                섹션은 ruleCode 화이트리스트 기반으로만 동작합니다. sortOrder 변경 시 입력 위치
                뒤의 섹션들이 자동으로 한 칸씩 밀립니다.
            </Alert>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <TextField
                        select
                        label="type 필터"
                        value={typeFilter}
                        onChange={(e) =>
                            setTypeFilter(e.target.value as '' | LibrarySectionType)
                        }
                        size="small"
                        sx={{ width: 200 }}
                    >
                        {TYPE_FILTER_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value || 'all'} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </CardContent>
            </Card>

            <Card>
                <CardContent sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={48} />
                                    <TableCell width={64}>#</TableCell>
                                    <TableCell width={120}>타입</TableCell>
                                    <TableCell>code</TableCell>
                                    <TableCell width={100}>sortOrder</TableCell>
                                    <TableCell width={100}>icon</TableCell>
                                    <TableCell width={160}>layoutHint</TableCell>
                                    <TableCell width={180}>ruleCode</TableCell>
                                    <TableCell width={80}>활성</TableCell>
                                    <TableCell width={160} align="right">
                                        작업
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedSections.map((s) => {
                                    const state = rowState[s.id];
                                    if (!state) return null;
                                    const isExpanded = expandedId === s.id;
                                    return (
                                        <Fragment key={s.id}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            setExpandedId(
                                                                isExpanded ? null : s.id
                                                            )
                                                        }
                                                    >
                                                        {isExpanded ? (
                                                            <ExpandLess />
                                                        ) : (
                                                            <ExpandMore />
                                                        )}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>{s.id}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={s.type}
                                                        size="small"
                                                        color={TYPE_CHIP_COLOR[s.type]}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <code>{s.code}</code>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={state.sortOrder}
                                                        onChange={(e) =>
                                                            handleChange(
                                                                s.id,
                                                                'sortOrder',
                                                                Number(e.target.value)
                                                            )
                                                        }
                                                        sx={{ width: 80 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={state.icon}
                                                        onChange={(e) =>
                                                            handleChange(
                                                                s.id,
                                                                'icon',
                                                                e.target.value
                                                            )
                                                        }
                                                        sx={{ width: 140 }}
                                                    >
                                                        <MenuItem value="">
                                                            <em>(없음)</em>
                                                        </MenuItem>
                                                        {LIBRARY_SECTION_ICON_OPTIONS.map(
                                                            (opt) => (
                                                                <MenuItem
                                                                    key={opt.code}
                                                                    value={opt.code}
                                                                >
                                                                    {opt.label}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </TextField>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={state.layoutHint}
                                                        onChange={(e) =>
                                                            handleChange(
                                                                s.id,
                                                                'layoutHint',
                                                                e.target
                                                                    .value as LibrarySectionLayoutHint
                                                            )
                                                        }
                                                        fullWidth
                                                    >
                                                        {LAYOUT_HINT_OPTIONS.map((hint) => (
                                                            <MenuItem
                                                                key={hint}
                                                                value={hint}
                                                            >
                                                                {hint}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </TableCell>
                                                <TableCell>
                                                    {s.type === 'DYNAMIC' ? (
                                                        <Chip
                                                            label={
                                                                state.ruleCode ??
                                                                '미지정'
                                                            }
                                                            size="small"
                                                            color={
                                                                state.ruleCode
                                                                    ? 'info'
                                                                    : 'default'
                                                            }
                                                        />
                                                    ) : (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.disabled"
                                                        >
                                                            -
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={state.isActive}
                                                        onChange={(e) =>
                                                            handleChange(
                                                                s.id,
                                                                'isActive',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack
                                                        direction="row"
                                                        gap={0.5}
                                                        justifyContent="flex-end"
                                                    >
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            startIcon={<Save />}
                                                            onClick={() =>
                                                                handleSaveRow(s)
                                                            }
                                                            disabled={
                                                                updateSection.isPending
                                                            }
                                                        >
                                                            저장
                                                        </Button>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDelete(s)}
                                                            disabled={
                                                                deleteSection.isPending
                                                            }
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                            {isExpanded && (
                                                <TableRow>
                                                    <TableCell />
                                                    <TableCell colSpan={9}>
                                                        <Stack gap={2} py={1}>
                                                            <Stack
                                                                direction={{
                                                                    xs: 'column',
                                                                    md: 'row',
                                                                }}
                                                                gap={2}
                                                            >
                                                                {TRANSLATION_LANGS.map(
                                                                    (lang) => (
                                                                        <TextField
                                                                            key={lang.lang}
                                                                            label={`${lang.label} (${lang.lang})`}
                                                                            size="small"
                                                                            value={
                                                                                state
                                                                                    .translations[
                                                                                    lang
                                                                                        .lang
                                                                                ] ?? ''
                                                                            }
                                                                            onChange={(e) =>
                                                                                handleTranslationChange(
                                                                                    s.id,
                                                                                    lang.lang,
                                                                                    e.target
                                                                                        .value
                                                                                )
                                                                            }
                                                                            fullWidth
                                                                        />
                                                                    )
                                                                )}
                                                            </Stack>
                                                            {s.type === 'DYNAMIC' && (
                                                                <TextField
                                                                    select
                                                                    size="small"
                                                                    label="ruleCode (서버 화이트리스트)"
                                                                    value={
                                                                        state.ruleCode ??
                                                                        ''
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleChange(
                                                                            s.id,
                                                                            'ruleCode',
                                                                            e.target.value
                                                                                ? e.target
                                                                                      .value
                                                                                : null
                                                                        )
                                                                    }
                                                                    fullWidth
                                                                    helperText="자유 텍스트 입력 불가 — 화이트리스트에서 선택"
                                                                >
                                                                    {dynamicRules.length ===
                                                                    0 ? (
                                                                        <MenuItem
                                                                            value=""
                                                                            disabled
                                                                        >
                                                                            (사용 가능한
                                                                            룰 없음)
                                                                        </MenuItem>
                                                                    ) : (
                                                                        dynamicRules.map(
                                                                            (r) => (
                                                                                <MenuItem
                                                                                    key={
                                                                                        r.ruleCode
                                                                                    }
                                                                                    value={
                                                                                        r.ruleCode
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        r.ruleCode
                                                                                    }{' '}
                                                                                    -{' '}
                                                                                    {
                                                                                        r.description
                                                                                    }
                                                                                </MenuItem>
                                                                            )
                                                                        )
                                                                    )}
                                                                </TextField>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Fragment>
                                    );
                                })}
                                {sortedSections.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            <Typography color="text.secondary" py={4}>
                                                섹션이 없습니다.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <LibrarySectionAddDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                nextSortOrder={nextSortOrder}
            />

            <Dialog
                open={deleteBlocked.open}
                onClose={() =>
                    setDeleteBlocked({
                        open: false,
                        sectionId: null,
                        sectionType: null,
                        message: '',
                    })
                }
            >
                <DialogTitle>섹션 삭제 차단</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        이 섹션을 사용하는 라이브러리가 있어 삭제할 수 없습니다.
                        먼저 해당 라이브러리에서{' '}
                        {deleteBlocked.sectionType === 'CURATION'
                            ? '큐레이션'
                            : '카테고리'}
                        를 변경/해제한 뒤 다시 시도하세요.
                    </DialogContentText>
                    {deleteBlocked.message && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {deleteBlocked.message}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setDeleteBlocked({
                                open: false,
                                sectionId: null,
                                sectionType: null,
                                message: '',
                            })
                        }
                    >
                        닫기
                    </Button>
                    <Button variant="contained" onClick={goToBlockedLibraries}>
                        라이브러리 목록 보기 →
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
